import type { Grounding } from "./grounding.js";
import { mockReply } from "./mock.js";
import { sanitiseHistory, validGuide, type ChatResponse, type ChatTurn } from "./prompt.js";

export interface ChatBackend {
  readonly mode: "live" | "mock";
  reply(history: ChatTurn[]): Promise<ChatResponse>;
}

export class MockBackend implements ChatBackend {
  readonly mode = "mock";
  constructor(private readonly g: Grounding, private readonly delayMs = 350) {}
  async reply(history: ChatTurn[]): Promise<ChatResponse> {
    await new Promise((r) => setTimeout(r, this.delayMs));
    return mockReply(this.g, history);
  }
}

export class WorkerBackend implements ChatBackend {
  readonly mode = "live";
  private readonly cid = Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
  constructor(private readonly url: string, private readonly g: Grounding, private readonly fetchImpl: typeof fetch = (...a) => fetch(...a)) {}
  async reply(history: ChatTurn[]): Promise<ChatResponse> {
    const turn = history.filter((m) => m.role === "user").length;
    const res = await this.fetchImpl(`${this.url}/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ characterId: this.g.characterId, messages: sanitiseHistory(history), turn, cid: this.cid })
    });
    const body = (await res.json().catch(() => ({}))) as Partial<ChatResponse> & { error?: string };
    if (!res.ok || typeof body.reply !== "string") throw new Error(body.error ?? `HTTP ${res.status}`);
    // Never trust the network for navigation targets: re-check against this character's non-archived guide places.
    const travelTo = validGuide(this.g, body.travelTo);
    return { reply: body.reply, ...(travelTo ? { travelTo } : {}), ...(body.ended ? { ended: true } : {}), ...(typeof body.sig === "string" ? { sig: body.sig } : {}) };
  }
}
